import React, { useRef, useState, useEffect } from "react";
import URI from "urijs";
import OktaSignIn from "@okta/okta-signin-widget";
import { Spinner } from "components/common";
import { FormGroup } from "components/bootstrap";
import CustomChosenStyles from "authentication/components/oidc/okta/external-assets/CustomChosenStyles";
import OktaContainerStyles from "authentication/components/oidc/okta/OktaContainerStyles";
const defaultRedirect = (url) => window.location.assign(url);
const OKTA_SCOPES = ["openid", "profile", "email"];
const OKTA_SCOPE = OKTA_SCOPES.join(" ");
const OktaLoginForm = ({
  configuration,
  nonce,
  checkSession = false,
  redirect = defaultRedirect
}) => {
  const widget = useRef();
  const [sessionState, setSessionState] = useState(checkSession ? "INITIAL" : "DONE");
  const widgetReady = useRef(false);
  const {
    oauth_base_url: baseUrl,
    oauth_auth_url: authorizeUrl,
    oauth_client_id: oauthClientId,
    oauth_callback_url: callbackUrl
  } = configuration;
  useEffect(() => {
    var _a;
    let shouldCleanUp = false;
    const cleanUp = () => {
      widget.current.off();
      widget.current.remove();
      widgetReady.current = false;
    };
    const onError = (err) => console.error(err);
    widget.current = new OktaSignIn({
      baseUrl,
      clientId: oauthClientId,
      redirectUri: callbackUrl,
      useClassicEngine: true,
      authParams: {
        issuer: baseUrl,
        authorizeUrl,
        scopes: OKTA_SCOPES,
        state: nonce.state,
        responseType: "code",
        pkce: false,
        // we don't use nonce on the backend so set to `null`
        nonce: null
      },
      language: "en",
      i18n: {
        en: {
          "primaryauth.username.placeholder": "Email",
          "primaryauth.username.tooltip": "Email",
          "password.forgot.email.or.username.placeholder": "Email",
          "password.forgot.email.or.username.tooltip": "Email",
          "error.username.required": "Please enter an email address",
          "errors.E0000004": "Invalid email or password",
          "mfa.factors.dropdown.sr.text": "Using {0}, verify another way?",
          "mfa.factors.dropdown.title": "Select a verification method",
          "email.code.not.received": "Haven't received an email? "
        }
      },
      features: {
        hideSignOutLinkInMFA: true,
        webauthn: true,
        multiOptionalFactorEnroll: false
      }
    });
    widget.current.on("ready", () => {
      if (shouldCleanUp) {
        cleanUp();
        return;
      }
      widgetReady.current = true;
    });
    widget.current.showSignInAndRedirect({ el: "#okta-root" }).catch(onError);
    if (checkSession) {
      setSessionState("CHECKING");
      (_a = widget.current.authClient) == null ? void 0 : _a.session.exists().then((exists) => {
        if (exists) {
          const loginUri = new URI(authorizeUrl);
          loginUri.query({
            client_id: oauthClientId,
            response_type: "code",
            scope: OKTA_SCOPE,
            redirect_uri: callbackUrl,
            state: nonce.state
          });
          redirect(loginUri.href());
        } else {
          setSessionState("DONE");
        }
      });
    }
    return () => {
      if (widget.current && widgetReady.current) {
        cleanUp();
        return;
      }
      shouldCleanUp = true;
    };
  }, [checkSession, oauthClientId, baseUrl, nonce.state, callbackUrl, authorizeUrl, redirect]);
  const isBusy = sessionState !== "DONE";
  return /* @__PURE__ */ React.createElement(FormGroup, null, isBusy && /* @__PURE__ */ React.createElement(Spinner, null), /* @__PURE__ */ React.createElement(CustomChosenStyles, null), /* @__PURE__ */ React.createElement(OktaContainerStyles, { key: "okta-root", id: "okta-root", style: { display: isBusy ? "none" : "block" } }));
};
export default OktaLoginForm;
